import { trapiClient } from './trapiClient';
import type { Environment } from '../../contexts/AppContext';

export class ApisService {
  private static readonly BASE_PATH = '/configs';

  static async getApis(params: { environment: Environment; accessToken: string; filter?: string }) {
    const { environment, accessToken, filter } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('type', 'ADMINCI');
    queryParams.append('ci', 'API');
    if (filter && filter.includes('=')) queryParams.append('filter', filter);
    const url = `${this.BASE_PATH}?${queryParams.toString()}`;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const response = await trapiClient.get<any[]>(url, accessToken);
    return Array.isArray(response) ? response : [];
  }

  static async getApi(params: { id: string; environment: Environment; accessToken: string }) {
    const { id, environment, accessToken } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
    return trapiClient.get<any>(url, accessToken);
  }

  static async getApiStats(environment: Environment, accessToken: string) {
    const apis = await this.getApis({ environment, accessToken });
    return {
      total: apis.length,
      active: apis.filter(a => Array.isArray(a.endpoints) && a.endpoints.length > 0).length,
      byFormat: apis.reduce((acc: Record<string, number>, api: any) => {
        const f = api.apiFormat || 'Unknown';
        acc[f] = (acc[f] || 0) + 1;
        return acc;
      }, {}),
      withSecurity: apis.filter(a => a.security && ((a.security.groups?.length ?? 0) > 0 || (a.security.identities?.length ?? 0) > 0)).length,
    };
  }

  static async upsertApi(params: { environment: Environment; accessToken: string; payload: any }) {
    const { environment, accessToken, payload } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('type', 'ADMINCI');
    queryParams.append('ci', 'API');
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);

    const body = { ...payload };
    // Ensure an id; derive from name/displayName if missing
    if (!body.id) {
      const basis: string = body.name || body.displayName || '';
      if (basis) body.id = basis.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    }

    if (body.id) {
      // Update (PUT)
      const url = `${this.BASE_PATH}/${encodeURIComponent(body.id)}?${queryParams.toString()}`;
      return trapiClient.put<any>(url, body, accessToken);
    }
    // Create (POST)
    const url = `${this.BASE_PATH}?${queryParams.toString()}`;
    return trapiClient.post<any>(url, body, accessToken);
  }

  static async deleteApi(params: { environment: Environment; accessToken: string; id: string }) {
  const { environment, accessToken, id } = params;
  if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
  const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
  return trapiClient.delete<any>(url, accessToken);
  }

  // Parity helpers used by ApiFormModal
  static async createApi(params: { environment: Environment; accessToken: string; data: any }) {
    const { environment, accessToken, data } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const body = { ...data, environment, ci: 'API' };
    // Creation is PUT without id in URL per config-ui behavior
    return trapiClient.put<any>(this.BASE_PATH, body, accessToken);
  }

  static async updateApi(params: { environment: Environment; accessToken: string; id: string; data: any }) {
    const { environment, accessToken, id, data } = params;
    if (trapiClient.getEnvironment() !== environment) trapiClient.setEnvironment(environment);
    const url = `${this.BASE_PATH}/${encodeURIComponent(id)}`;
    return trapiClient.put<any>(url, data, accessToken);
  }
}
